'use client';

import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { useLanguage } from '@/context/LanguageContext';

export default function Header() {
  const router = useRouter();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close menu on route change / resize
  useEffect(() => {
    if (menuOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const navItems = [
    { label: t('header.services'), href: '/home#services' },
    { label: t('header.pricing'), href: '/pricing' },
    { label: t('header.faq'), href: '/faq' },
    { label: t('header.contact'), href: '/home#contact' },
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-blackout/90 backdrop-blur-md border-b border-white/5'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          {/* Logo */}
          <button
            onClick={() => router?.push('/home')}
            className="flex items-center group flex-shrink-0"
            aria-label="Nexavi home"
          >
            <span
              className="text-monitor font-black tracking-widest uppercase text-xs sm:text-sm"
              style={{ letterSpacing: '0.2em' }}
            >
              Nexavi
            </span>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6 sm:gap-8">
            {navItems?.map((item) => (
              <Link
                key={item?.label}
                href={item?.href}
                className="text-xs font-semibold uppercase tracking-widest text-monitor/50 hover:text-gold transition-colors duration-300"
              >
                {item?.label}
              </Link>
            ))}
          </nav>

          {/* Right side: Language selector + CTA + Burger */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Language Selector */}
            <div className="flex items-center gap-1 border border-white/10 rounded-full px-1 py-1 bg-white/5">
              <button
                onClick={() => setLanguage('en')}
                className={`text-xs font-bold uppercase tracking-widest px-2 sm:px-3 py-1 rounded-full transition-all duration-200 ${
                  language === 'en'
                    ? 'bg-gold text-blackout'
                    : 'text-monitor/40 hover:text-monitor'
                }`}
              >
                EN
              </button>

              <button
                onClick={() => setLanguage('da')}
                className={`text-xs font-bold uppercase tracking-widest px-2 sm:px-3 py-1 rounded-full transition-all duration-200 ${
                  language === 'da'
                    ? 'bg-gold text-blackout'
                    : 'text-monitor/40 hover:text-monitor'
                }`}
              >
                DA
              </button>
            </div>

            {/* CTA — hidden on mobile to save space */}
            <a
              href="#contact"
              className="hidden md:inline-block gold-shimmer-btn text-blackout text-xs font-black uppercase tracking-widest px-3 sm:px-5 py-2 sm:py-2.5 rounded-full transition-all hover:shadow-gold-glow whitespace-nowrap"
            >
              {t('header.getInTouch')}
            </a>

            {/* Burger button — mobile only */}
            <button
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              className="md:hidden flex flex-col justify-center items-center w-9 h-9 gap-1.5 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-colors duration-200 flex-shrink-0"
            >
              <span
                className={`block w-4.5 h-0.5 bg-monitor rounded-full transition-all duration-300 origin-center ${
                  menuOpen ? 'rotate-45 translate-y-2' : ''
                }`}
                style={{ width: '18px' }}
              />

              <span
                className={`block h-0.5 bg-monitor rounded-full transition-all duration-300 ${
                  menuOpen ? 'opacity-0 scale-x-0' : 'opacity-100'
                }`}
                style={{ width: '18px' }}
              />

              <span
                className={`block h-0.5 bg-monitor rounded-full transition-all duration-300 origin-center ${
                  menuOpen ? '-rotate-45 -translate-y-2' : ''
                }`}
                style={{ width: '18px' }}
              />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-opacity duration-300 ${
          menuOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMenuOpen(false)}
        style={{
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(4px)',
        }}
      />

      {/* Mobile Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-72 z-50 md:hidden flex flex-col transition-transform duration-300 ease-in-out ${
          menuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          background: 'rgba(10,10,10,0.97)',
          borderLeft: '1px solid rgba(255,255,255,0.07)',
        }}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/5">
          <span
            className="text-monitor font-black tracking-widest uppercase text-xs"
            style={{ letterSpacing: '0.2em' }}
          >
            Nexavi
          </span>

          <button
            onClick={() => setMenuOpen(false)}
            aria-label="Close menu"
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-colors"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
            >
              <path
                d="M1 1l12 12M13 1L1 13"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                className="text-monitor/60"
              />
            </svg>
          </button>
        </div>

        {/* Nav links */}
        <nav className="flex flex-col px-6 py-6 gap-1 flex-1">
          {navItems?.map((item, i) => (
            <Link
              key={item?.label}
              href={item?.href}
              onClick={() => setMenuOpen(false)}
              className="text-sm font-semibold uppercase tracking-widest text-monitor/60 hover:text-gold py-3 border-b border-white/5 transition-colors duration-200"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              {item?.label}
            </Link>
          ))}

          {/* CTA inside drawer */}
          <a
            href="/home#contact"
            onClick={() => setMenuOpen(false)}
            className="mt-6 gold-shimmer-btn text-blackout text-xs font-black uppercase tracking-widest px-5 py-3 rounded-full text-center transition-all hover:shadow-gold-glow"
          >
            {t('header.getInTouch')}
          </a>
        </nav>
      </div>
    </>
  );
}